import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import GlassCard from "@/components/GlassCard";
import { SplitText, BlurText, SpotlightCard, Magnet, CountUp, Aurora } from "@/components/animations";
import api from "@/lib/api";
import { Search, ArrowRight, Users, TrendingUp, GraduationCap, MapPin } from "lucide-react";

const TIER_LABELS = { tier_1: "Tier 1", tier_2: "Tier 2", tier_3: "Tier 3" };
const TIER_COLORS = { tier_1: "text-green-pioneer", tier_2: "text-amber-builder", tier_3: "text-text-secondary" };

export default function PersonaEstimate() {
    const [college, setCollege] = useState("");
    const [course, setCourse] = useState("engineering");
    const [year, setYear] = useState(3);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState("");

    const handleEstimate = async (e) => {
        e.preventDefault();
        setLoading(true);
        setSubmitted(true);
        setError("");
        try {
            const res = await api.get(`/api/persona/estimate?college=${encodeURIComponent(college)}&course=${course}&year=${year}`);
            setResult(res.data);
        } catch (err) {
            setResult(null);
            setError(err.response?.data?.detail || "Could not estimate right now.");
        }
        setLoading(false);
    };

    return (
        <div className="pt-24 pb-16 px-6 md:px-12 lg:px-24 min-h-dvh" data-testid="persona-estimate">
            <div className="max-w-3xl mx-auto">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                    <p className="text-xs uppercase tracking-[0.3em] text-purple-soft font-semibold mb-4">Quick Estimate</p>
                    <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter text-text-primary mb-4">
                        Estimate Your Credit Potential
                    </h1>
                    <p className="text-text-secondary text-base md:text-lg mb-10 leading-relaxed max-w-xl">
                        Answer three quick questions to see where students like you typically score. No account required.
                    </p>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
                    <GlassCard className="p-6 md:p-8 mb-8" hover={false}>
                        <form onSubmit={handleEstimate} className="space-y-5">
                            <div>
                                <label className="text-xs uppercase tracking-widest text-text-secondary font-semibold mb-2 block">
                                    College / University
                                </label>
                                <div className="relative">
                                    <GraduationCap size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-secondary/50" />
                                    <input type="text" value={college} onChange={e => setCollege(e.target.value)}
                                        placeholder="e.g. IIT Bombay or VIT Vellore"
                                        className="glass-input pl-10 text-sm"
                                        data-testid="persona-college-input" />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs uppercase tracking-widest text-text-secondary font-semibold mb-2 block">
                                        Course Category
                                    </label>
                                    <select value={course} onChange={e => setCourse(e.target.value)}
                                        className="glass-input text-sm" data-testid="persona-course-select">
                                        <option value="engineering">Engineering / CS / Tech</option>
                                        <option value="business">Business / MBA / Commerce</option>
                                        <option value="arts_science">Arts & Science</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-widest text-text-secondary font-semibold mb-2 block">
                                        Year of Study
                                    </label>
                                    <select value={year} onChange={e => setYear(Number(e.target.value))}
                                        className="glass-input text-sm" data-testid="persona-year-select">
                                        {[1, 2, 3, 4].map(y => <option key={y} value={y}>Year {y}</option>)}
                                    </select>
                                </div>
                            </div>
                            <button type="submit" disabled={loading}
                                className="bg-purple-soft text-white font-medium px-8 py-3 rounded-xl hover:bg-purple-soft/90 hover:shadow-[0_0_20px_rgba(139,92,246,0.4)] transition-all text-sm inline-flex items-center gap-2 disabled:opacity-50"
                                data-testid="persona-estimate-submit">
                                <Search size={16} /> {loading ? "Estimating..." : "Estimate My Range"}
                            </button>
                        </form>
                    </GlassCard>
                </motion.div>

                {submitted && result && (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                        <GlassCard className="p-6 md:p-8 border border-purple-soft/20" hover={false}>
                            <div className="flex items-center gap-2 mb-6">
                                <TrendingUp size={18} className="text-purple-soft" />
                                <h3 className="font-heading text-lg font-medium text-text-primary">Your Estimated Range</h3>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                                <div>
                                    <p className="text-text-secondary text-xs mb-2">Estimated Score Range</p>
                                    <p className="font-mono text-4xl font-bold text-cyan-signal tracking-tighter" data-testid="estimated-range">
                                        {result.estimated_score_range?.[0]}–{result.estimated_score_range?.[1]}
                                    </p>
                                    <p className="text-text-secondary text-xs mt-1">out of 300–900</p>
                                </div>
                                <div>
                                    <p className="text-text-secondary text-xs mb-2">Cohort Average</p>
                                    <p className="font-mono text-4xl font-bold text-text-primary tracking-tighter" data-testid="cohort-avg">
                                        {result.avg_score}
                                    </p>
                                    <p className="text-text-secondary text-xs mt-1">average score in cohort</p>
                                </div>
                            </div>

                            <div className="space-y-3 p-4 bg-white/[0.02] border border-white/5 rounded-xl mb-6">
                                <div className="flex items-center gap-3">
                                    <MapPin size={14} className="text-text-secondary" />
                                    <div>
                                        <span className="text-text-secondary text-xs">Institution: </span>
                                        <span className="text-text-primary text-sm font-medium">{result.institution}</span>
                                        <span className={`ml-2 text-xs font-semibold ${TIER_COLORS[result.institution_tier] || "text-text-secondary"}`}>
                                            {TIER_LABELS[result.institution_tier] || result.institution_tier}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Users size={14} className="text-text-secondary" />
                                    <div>
                                        <span className="text-text-secondary text-xs">Cohort: </span>
                                        <span className="text-text-primary text-sm font-medium">
                                            {result.cohort?.label || `${result.course_category} Year ${result.year}`}
                                        </span>
                                        {result.cohort?.cohort_size && (
                                            <span className="text-text-secondary text-xs ml-2">
                                                ({result.cohort.cohort_size.toLocaleString()} students)
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-3">
                                <Link to="/genz-credit-scoring/flow"
                                    className="bg-blue-primary text-white font-medium px-6 py-2.5 rounded-xl hover:bg-blue-primary/90 hover:shadow-[0_0_20px_rgba(59,130,246,0.4)] transition-all text-sm inline-flex items-center gap-2"
                                    data-testid="persona-get-real-score">
                                    Get Your Real Score <ArrowRight size={16} />
                                </Link>
                                <Link to="/genz-credit-scoring"
                                    className="bg-elevated text-text-primary border border-white/10 px-6 py-2.5 rounded-xl hover:bg-white/5 transition-all text-sm font-medium"
                                    data-testid="persona-learn-more">
                                    Learn More
                                </Link>
                            </div>
                        </GlassCard>
                    </motion.div>
                )}

                {submitted && !result && !loading && (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                        <GlassCard className="p-6 text-center">
                            <p className="text-text-secondary text-sm">{error || "Could not estimate. Try a different college name."}</p>
                        </GlassCard>
                    </motion.div>
                )}
            </div>
        </div>
    );
}
